import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  Upload,
  message,
  Space,
  Row,
  Col,
  Tag,
  Divider,
  Alert,
  Spin
} from 'antd';
import {
  DollarCircleOutlined,
  UploadOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  BankOutlined,
  FileImageOutlined,
  DeleteOutlined,
  EyeOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import PersianDatePicker from '../PersianDatePicker';
import { financialApi } from '../../utils/financialApi';
import { extractApiUserMessage } from '../../utils/api';
import { formatPriceToman } from '../../utils/formatPriceToman';
import { toJalali } from '../../utils/dateUtils';
import moment from 'moment-jalaali';

const { Option } = Select;
const { TextArea } = Input;
const { Dragger } = Upload;

const toLatinDigits = (value) => {
  if (!value) return value;
  return String(value).replace(/[۰-۹]/g, (w) => String.fromCharCode(w.charCodeAt(0) - 1728));
};

const amountToPersianWords = (n) => {
  if (!n || isNaN(n)) return '';
  n = Math.floor(Number(n));
  if (n === 0) return 'صفر تومان';
  const units = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'];
  const teens = ['ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نوزده'];
  const tens = ['', 'ده', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'];
  const hundreds = ['', 'یکصد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد'];
  const scales = ['', 'هزار', 'میلیون', 'میلیارد', 'تریلیون'];

  function chunkToWords(num) {
    let parts = [];
    const h = Math.floor(num / 100);
    const rem = num % 100;
    if (h > 0) parts.push(hundreds[h]);
    if (rem >= 10 && rem <= 19) {
      parts.push(teens[rem - 10]);
    } else {
      const t = Math.floor(num / 10);
      const u = rem % 10;
      if (t > 0) parts.push(tens[t]);
      if (u > 0) parts.push(units[u]);
    }
    return parts.join(' و ');
  }

  let chunks = [];
  let temp = n;
  while (temp > 0) {
    chunks.push(temp % 1000);
    temp = Math.floor(temp / 1000);
  }

  let resParts = [];
  for (let i = chunks.length - 1; i >= 0; i--) {
    const chunk = chunks[i];
    if (chunk > 0) {
      const words = chunkToWords(chunk);
      const scale = scales[i];
      resParts.push(scale ? words + ' ' + scale : words);
    }
  }
  return resParts.join(' و ') + ' تومان';
};

const RegisterDealPaymentModal = ({
  open,
  onClose,
  deal,
  onSuccess,
  editPayment = null,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [previewImage, setPreviewImage] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [enteredAmount, setEnteredAmount] = useState(null);
  // سقف قابل ثبت: ارزش معامله منهای واریزی‌های تاییدشده و در انتظار تایید (از سرور)
  const [availableAmount, setAvailableAmount] = useState(null);

  const dealId = deal?.id;
  const dealValue = Number(deal?.value || 0);
  const paidAmount = Number(deal?.paid_amount || 0);
  const remainingAmount = Math.max(0, deal?.remaining_amount != null ? Number(deal.remaining_amount) : dealValue - paidAmount);
  const maxAmount = availableAmount != null
    ? availableAmount + (editPayment ? Number(editPayment.amount_raw ?? editPayment.amount ?? 0) : 0)
    : null;
  const fillableAmount = maxAmount != null ? maxAmount : remainingAmount;

  // Load bank accounts and installments when modal opens
  useEffect(() => {
    if (!open || !dealId) return;

    let isMounted = true;
    setLoadingData(true);

    // Initial defaults
    form.resetFields();
    setAvailableAmount(null);
    setFileList([]);
    setEnteredAmount(remainingAmount > 0 ? remainingAmount : null);

    form.setFieldsValue({
      amount: remainingAmount > 0 ? remainingAmount : undefined,
      payment_method_type: 'card_to_card',
      payment_purpose: paidAmount > 0 ? (remainingAmount <= 0 ? 'settlement' : 'installment') : 'down_payment',
      sales_channel: 'crm',
      paid_on: moment().format('jYYYY/jMM/jDD'),
    });

    // حالت اصلاح واریزی «نیاز به اصلاح»: پر کردن فرم با اطلاعات قبلی
    if (editPayment) {
      const amt = Number(editPayment.amount_raw ?? editPayment.amount ?? 0) || undefined;
      setEnteredAmount(amt || null);
      form.setFieldsValue({
        amount: amt,
        payment_method_type: editPayment.payment_method_type || 'card_to_card',
        payment_purpose: editPayment.payment_purpose || undefined,
        sales_channel: editPayment.sales_channel || 'crm',
        paid_on: editPayment.paid_on_jalali || (editPayment.paid_on ? toJalali(editPayment.paid_on) : undefined),
        tracking_code: editPayment.tracking_code || undefined,
        bank_account_id: editPayment.bank_account_id || undefined,
        remarks: editPayment.remarks || undefined,
        next_installment_amount: editPayment.next_installment_amount ? Number(editPayment.next_installment_amount) : undefined,
        next_installment_date: editPayment.next_installment_date ? toJalali(editPayment.next_installment_date) : undefined,
      });
    }

    const fetchData = async () => {
      try {
        const [dealsRes, instRes] = await Promise.allSettled([
          financialApi.getDealsForPayment(),
          financialApi.getDealInstallments(dealId),
        ]);

        if (!isMounted) return;

        if (dealsRes.status === 'fulfilled' && dealsRes.value?.data?.bank_accounts) {
          setBankAccounts(dealsRes.value.data.bank_accounts);
        }

        if (dealsRes.status === 'fulfilled') {
          const found = (dealsRes.value?.data?.deals || []).find((d) => Number(d.id) === Number(dealId));
          if (found && found.available_amount != null) {
            const avail = Number(found.available_amount);
            setAvailableAmount(avail);
            if (!editPayment) {
              form.setFieldsValue({ amount: avail > 0 ? avail : undefined });
              setEnteredAmount(avail > 0 ? avail : null);
            }
          } else {
            setAvailableAmount(null);
          }
        }

        if (instRes.status === 'fulfilled') {
          const rawInst = instRes.value?.data?.installments || instRes.value?.installments || [];
          setInstallments(rawInst.filter((i) => i.status !== 'paid'));
        }
      } catch (err) {
        console.warn('Error loading payment metadata:', err);
      } finally {
        if (isMounted) setLoadingData(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [open, dealId, form, remainingAmount, paidAmount, editPayment]);

  const handleInstallmentChange = (instId) => {
    if (!instId) return;
    const inst = installments.find((i) => Number(i.id) === Number(instId));
    if (inst) {
      const instAmount = Number(inst.amount || 0);
      form.setFieldsValue({
        amount: instAmount,
        payment_purpose: 'installment',
      });
      setEnteredAmount(instAmount);
    }
  };

  const handleFillRemaining = () => {
    if (fillableAmount > 0) {
      form.setFieldsValue({ amount: fillableAmount });
      setEnteredAmount(fillableAmount);
      message.info(`مبلغ روی کل مانده قابل ثبت (${formatPriceToman(fillableAmount)}) تنظیم شد.`);
    }
  };

  const handleBeforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (isImage) {
      file.preview = URL.createObjectURL(file);
    }
    setFileList([file]);
    return false; // prevent auto upload
  };

  const handlePreview = (file) => {
    if (file.preview || file.url) {
      setPreviewImage(file.preview || file.url);
      setPreviewVisible(true);
    } else {
      message.info('پیش‌نمایش برای این نوع فایل در دسترس نیست');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const formData = new FormData();
      formData.append('amount', Number(toLatinDigits(values.amount)));
      formData.append('payment_method_type', values.payment_method_type);
      if (values.sales_channel) formData.append('sales_channel', values.sales_channel);
      if (values.tracking_code) formData.append('tracking_code', toLatinDigits(values.tracking_code).trim());
      if (values.payment_purpose) formData.append('payment_purpose', values.payment_purpose);
      if (values.bank_account_id) formData.append('bank_account_id', Number(values.bank_account_id));
      if (values.paid_on) {
        let rawDate = values.paid_on;
        if (typeof rawDate === 'object' && rawDate?.format) {
          rawDate = rawDate.format('YYYY/MM/DD');
        } else if (rawDate instanceof Date) {
          rawDate = toJalali(rawDate);
        }
        formData.append('paid_on', toLatinDigits(rawDate));
      }
      if (values.remarks) formData.append('remarks', values.remarks);
      if (values.next_installment_amount && values.next_installment_date) {
        let nd = values.next_installment_date;
        if (typeof nd === 'object' && nd?.format) {
          nd = nd.format('YYYY/MM/DD');
        } else if (nd instanceof Date) {
          nd = toJalali(nd);
        }
        formData.append('next_installment_amount', Number(toLatinDigits(values.next_installment_amount)));
        formData.append('next_installment_date', toLatinDigits(nd));
      }
      if (values.deal_installment_id) formData.append('deal_installment_id', values.deal_installment_id);

      if (fileList.length > 0) {
        formData.append('receipt_file', fileList[0].originFileObj || fileList[0]);
      }

      if (editPayment) {
        await financialApi.resubmitDealPayment(dealId, editPayment.id, formData);
        message.success('واریزی اصلاح شد و مجدداً به کارتابل مدیر مالی ارسال گردید.');
      } else {
        await financialApi.registerDealPayment(dealId, formData);
        message.success('واریزی با موفقیت ثبت شد و به کارتابل مدیر مالی ارسال گردید.');
      }

      form.resetFields();
      setFileList([]);
      setEnteredAmount(null);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      if (err?.errorFields) {
        message.error('لطفاً فیلدهای الزامی را تکمیل کنید');
      } else {
        message.error(extractApiUserMessage(err) || 'خطا در ثبت واریزی معامله');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        title={
          <div className="flex items-center gap-2 text-gray-800 dark:text-gray-100 font-bold" style={{ fontFamily: 'CustomFont' }}>
            <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 flex items-center justify-center text-lg">
              <DollarCircleOutlined />
            </div>
            <span>{editPayment ? `اصلاح واریزی معامله #${dealId}` : `ثبت واریزی معامله #${dealId}`}</span>
          </div>
        }
        open={open}
        onCancel={onClose}
        width={760}
        style={{ fontFamily: 'CustomFont' }}
        footer={[
          <Button key="cancel" onClick={onClose} disabled={submitting} style={{ fontFamily: 'CustomFont' }}>
            انصراف
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={submitting}
            onClick={handleSubmit}
            className="bg-emerald-600 hover:bg-emerald-500 font-semibold rounded-lg"
            style={{ fontFamily: 'CustomFont' }}
          >
            {editPayment ? 'اصلاح و ارسال مجدد به مدیر مالی' : 'ثبت و ارسال به کارتابل تأیید'}
          </Button>,
        ]}
      >
        <Spin spinning={loadingData}>
          {editPayment?.rejection_reason && (
            <Alert
              type="warning"
              showIcon
              className="mb-4"
              message="دلیل درخواست اصلاح از مدیر مالی"
              description={editPayment.rejection_reason}
            />
          )}
          {/* Deal Summary Banner */}
          <div className="mb-4 rounded-xl border border-emerald-100 dark:border-emerald-800/60 bg-emerald-50/50 dark:bg-emerald-950/20 p-3">
            <div className="mb-3 text-sm">
              <span className="text-gray-500 dark:text-gray-400">معامله: </span>
              <span className="font-bold text-gray-800 dark:text-gray-100">{deal?.name}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg bg-white dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 px-3 py-2 text-center">
                <div className="text-[11px] text-gray-500 mb-0.5">ارزش کل</div>
                <div className="text-sm font-bold text-gray-800 dark:text-gray-200">{formatPriceToman(dealValue)}</div>
              </div>
              <div className="rounded-lg bg-white dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 px-3 py-2 text-center">
                <div className="text-[11px] text-gray-500 mb-0.5">وصول‌شده</div>
                <div className="text-sm font-bold text-emerald-600">{formatPriceToman(paidAmount)}</div>
              </div>
              <div className="rounded-lg bg-white dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 px-3 py-2 text-center">
                <div className="text-[11px] text-gray-500 mb-0.5">مانده</div>
                <div className="text-sm font-bold text-red-500">{formatPriceToman(remainingAmount)}</div>
              </div>
            </div>
          </div>

          <Form
            form={form}
            layout="vertical"
            style={{ fontFamily: 'CustomFont' }}
            initialValues={{
              payment_method_type: 'card_to_card',
              payment_purpose: 'installment',
              sales_channel: 'crm',
              paid_on: moment().format('jYYYY/jMM/jDD'),
            }}
          >
            <Row gutter={16}>
              {/* مبلغ واریزی */}
              <Col xs={24}>
                <Form.Item
                  name="amount"
                  label={<span className="font-medium">مبلغ واریزی (تومان) <span className="text-red-500">*</span></span>}
                  rules={[
                    { required: true, message: 'لطفاً مبلغ واریزی را وارد کنید' },
                    () => ({
                      validator: (_, v) => (maxAmount != null && v && Number(v) > maxAmount + 0.001)
                        ? Promise.reject(new Error(maxAmount > 0
                          ? `مبلغ بیشتر از سقف قابل ثبت است (حداکثر ${formatPriceToman(maxAmount)})`
                          : 'ارزش معامله با واریزی‌های ثبت‌شده کامل پوشش داده شده است'))
                        : Promise.resolve(),
                    }),
                  ]}
                  style={{ marginBottom: 8 }}
                >
                  <InputNumber
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    size="large"
                    min={1}
                    formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(val) => val.replace(/\$\s?|(,*)/g, '')}
                    onChange={(val) => setEnteredAmount(val)}
                    placeholder="مبلغ به تومان"
                  />
                </Form.Item>
                <div className="mb-4 flex flex-nowrap items-center justify-between gap-2 min-h-[24px] whitespace-nowrap overflow-hidden">
                  {enteredAmount > 0 ? (
                    <span className="text-xs text-emerald-700 dark:text-emerald-400 truncate min-w-0">
                      <span className="font-semibold">حروف: </span>
                      {amountToPersianWords(enteredAmount)}
                    </span>
                  ) : <span />}
                  {fillableAmount > 0 && (
                    <button
                      type="button"
                      onClick={handleFillRemaining}
                      className="shrink-0 text-xs text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded cursor-pointer border-0"
                    >
                      پر کردن با کل مانده ({formatPriceToman(fillableAmount)})
                    </button>
                  )}
                </div>
              </Col>

            </Row>

            <Row gutter={16}>
              {/* روش پرداخت */}
              <Col xs={24} sm={12}>
                <Form.Item
                  name="payment_method_type"
                  label={<span className="font-medium">روش پرداخت <span className="text-red-500">*</span></span>}
                  rules={[{ required: true, message: 'لطفاً روش پرداخت را انتخاب کنید' }]}
                >
                  <Select size="middle" placeholder="انتخاب روش پرداخت" dropdownStyle={{ fontFamily: 'CustomFont' }}>
                    <Option value="card_to_card">کارت به کارت</Option>
                    <Option value="bank_transfer">واریز به حساب / پایا / ساتنا</Option>
                    <Option value="pos">کارتخوان (POS)</Option>
                    <Option value="cheque">چک صیادی</Option>
                    <Option value="gateway">درگاه آنلاین</Option>
                    <Option value="cash">نقدی</Option>
                  </Select>
                </Form.Item>
              </Col>

              {/* حساب بانکی شرکت */}
              <Col xs={24} sm={12}>
                <Form.Item
                  name="bank_account_id"
                  label={<span className="font-medium">حساب بانکی مقصد</span>}
                >
                  <Select
                    size="middle"
                    placeholder="انتخاب حساب بانکی"
                    allowClear
                    dropdownStyle={{ fontFamily: 'CustomFont' }}
                  >
                    {bankAccounts.map((b) => (
                      <Option key={b.id} value={b.id}>
                        {b.bank_name || 'بانک'} - {b.account_number || b.card_number || `#${b.id}`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              {/* تاریخ واریز */}
              <Col xs={24} sm={12}>
                <Form.Item
                  name="paid_on"
                  label={<span className="font-medium">تاریخ پرداخت <span className="text-red-500">*</span></span>}
                  rules={[{ required: true, message: 'لطفاً تاریخ پرداخت را مشخص کنید' }]}
                >
                  <PersianDatePicker
                    placeholder="انتخاب تاریخ واریز"
                    className="w-full"
                    style={{ height: '40px' }}
                  />
                </Form.Item>
              </Col>
              {/* شماره پیگیری / ارجاع */}
              <Col xs={24} sm={12}>
                <Form.Item
                  name="tracking_code"
                  label={<span className="font-medium">شماره پیگیری / کد ارجاع / شناسه واریز</span>}
                >
                  <Input
                    placeholder="کد رهگیری فیش بانکی"
                    style={{ fontFamily: 'CustomFont' }}
                  />
                </Form.Item>
              </Col>

            </Row>

            <Row gutter={16}>
              {/* بابت پرداخت */}
              <Col xs={24} sm={12}>
                <Form.Item
                  name="payment_purpose"
                  label={<span className="font-medium">بابت پرداخت (هدف واریزی)</span>}
                >
                  <Select size="middle" placeholder="هدف پرداخت" dropdownStyle={{ fontFamily: 'CustomFont' }}>
                    <Option value="down_payment">پیش‌پرداخت اولیه</Option>
                    <Option value="installment">پرداخت قسط معامله</Option>
                    <Option value="settlement">تسویه حساب نهایی</Option>
                    <Option value="on_account">علی‌الحساب</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="sales_channel"
                  label={<span className="font-medium">کانال ارتباطی</span>}
                >
                  <Select size="middle" dropdownStyle={{ fontFamily: 'CustomFont' }}>
                    <Option value="crm">حضوری / سیستم CRM</Option>
                    <Option value="phone">تماس تلفنی</Option>
                    <Option value="whatsapp">واتساپ / تلگرام</Option>
                    <Option value="website">سایت</Option>
                  </Select>
                </Form.Item>
              </Col>

            </Row>

            {/* در صورت وجود اقساط معامله */}
            {installments.length > 0 && (
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item
                    name="deal_installment_id"
                    label={<span className="font-medium text-blue-600">اتصال به قسط معامله (اختیاری)</span>}
                  >
                    <Select
                      size="middle"
                      placeholder="اگر این واریزی بابت قسط خاصی است، انتخاب کنید"
                      allowClear
                      onChange={handleInstallmentChange}
                      dropdownStyle={{ fontFamily: 'CustomFont' }}
                    >
                      {installments.map((i) => (
                        <Option key={i.id} value={i.id}>
                          قسط شماره {i.installment_number} - مبلغ {formatPriceToman(i.amount)} {i.due_date ? `(سررسید: ${toJalali(i.due_date)})` : ''}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            )}


            {/* قسط بعدی (اختیاری): پس از تایید مالی به بدهی‌ها اضافه می‌شود */}
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="next_installment_amount"
                  label={<span className="font-medium">مبلغ قسط بعدی (تومان - اختیاری)</span>}
                >
                  <InputNumber
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    min={1}
                    formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(val) => val.replace(/\$\s?|(,*)/g, '')}
                    placeholder="مبلغ قسط بعدی"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="next_installment_date"
                  label={<span className="font-medium">تاریخ تقریبی پرداخت قسط بعدی</span>}
                  help="قسط بعدی فقط در صورت پر بودن هر دو فیلد (مبلغ و تاریخ) ثبت می‌شود."
                >
                  <PersianDatePicker placeholder="انتخاب تاریخ" className="w-full" style={{ height: '40px' }} />
                </Form.Item>
              </Col>
            </Row>

            {/* توضیحات و یادداشت برای مدیر مالی (تمام‌عرض) */}
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  name="remarks"
                  label={<span className="font-medium">توضیحات و یادداشت برای مدیر مالی</span>}
                >
                  <Input
                    placeholder="توضیحات اختیاری درباره واریزی..."
                    style={{ fontFamily: 'CustomFont' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* آپلود فیش */}
            <Form.Item label={<span className="font-medium">تصویر فیش یا سند واریز</span>}>
              <Dragger
                name="receipt_file"
                multiple={false}
                fileList={fileList}
                beforeUpload={handleBeforeUpload}
                onRemove={() => setFileList([])}
                onPreview={handlePreview}
                accept="image/*,application/pdf"
                className="bg-gray-50 dark:bg-gray-800/40 rounded-xl"
              >
                <p className="ant-upload-drag-icon text-emerald-500 mb-1">
                  <InboxOutlined className="text-2xl" />
                </p>
                <p className="ant-upload-text text-xs text-gray-700 dark:text-gray-300 font-medium">
                  برای بارگذاری فیش بانکی، فایل را اینجا بکشید یا کلیک کنید
                </p>
                <p className="ant-upload-hint text-[11px] text-gray-400">
                  فرمت‌های مجاز: JPG, PNG, PDF (حداکثر ۱۰ مگابایت)
                </p>
              </Dragger>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        centered
        width={600}
      >
        <img alt="فیش واریز" style={{ width: '100%', borderRadius: 8 }} src={previewImage} />
      </Modal>
    </>
  );
};

export default RegisterDealPaymentModal;
